/**
 * Commercial project domain field catalog (acf_groups_json structure).
 */

import {
  ASBUILT_EDIT_ROLE_PATTERNS,
  ENGINEERING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS,
  ENGINEERING_PERMITS_EDIT_ROLE_PATTERNS,
  ENGINEERING_ROUTE_UPLOAD_ROLE_PATTERNS,
  INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS,
  OSP_ENGINEERING_SITE_WALK_EDIT_ROLE_PATTERNS,
  USA_TICKET_EDIT_ROLE_PATTERNS,
  canEditOspEngineeringSiteWalkFiles,
  expansionContractFieldVisible,
  expansionFieldRoleVisible,
} from "./commercialRoleAccess";
import { PERMITS_REPEATER_FIELD } from "./commercialPermitsSchema";

export const COMMERCIAL_DOMAINS = {
  SUMMARY: "project_summary",
  ENGINEERING: "engineering_details",
  CONSTRUCTION: "construction_details",
  FIBER: "fiber_details",
  NETWORKING: "networking_details",
  ACCOUNTING: "accounting_details",
};

export const COMMERCIAL_DOMAIN_LIST = Object.values(COMMERCIAL_DOMAINS);

export const PROJECT_SUMMARY_TYPE_OPTIONS = ["Revenue", "Non-Revenue", "Capital"];

export function isRevenueCommercialProjectType(projectType) {
  return String(projectType || "")
    .trim()
    .toLowerCase() === "revenue";
}

/** Non-Revenue or Capital — not delivering fiber service to a paying customer. */
export function isNonRevenueCommercialProjectType(projectType) {
  const t = String(projectType || "")
    .trim()
    .toLowerCase();
  return t === "non-revenue" || t === "capital";
}

export function isCommercialIncidentFlag(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

/** Fiber hit / outage / similar — only on Non-Revenue or Capital Overview. */
export function commercialIncidentToggleVisible(ctx) {
  return isNonRevenueCommercialProjectType(ctx?.summary?.type ?? ctx?.summary?.work_order_type);
}

/** Detail fields appear once the incident toggle is on. */
export function commercialIncidentDetailsVisible(ctx) {
  return commercialIncidentToggleVisible(ctx) && isCommercialIncidentFlag(ctx?.summary?.incident);
}

/** Hide service-delivery fields that do not apply to fiber-hit / incident work. */
export function commercialNonIncidentFieldVisible(ctx) {
  return !isCommercialIncidentFlag(ctx?.summary?.incident);
}

/** Fields that only apply once Fiber hit / incident is checked. */
export function commercialIncidentOnlyFieldVisible(ctx) {
  return isCommercialIncidentFlag(ctx?.summary?.incident);
}

export const INCIDENT_TYPE_OPTIONS = [
  { value: "fiber_hit", label: "Fiber Hit" },
  { value: "conduit_hit", label: "Conduit Hit" },
  { value: "rodent_chew", label: "Rodent Chew" },
  { value: "vandalism", label: "Vandalism" },
  { value: "third_party_outage", label: "Third-Party Circuit Outage" },
  { value: "other", label: "Other" },
];

export const PROJECT_SUMMARY_SUBTYPE_OPTIONS = ["Upgrade", "Renewal", "New"];
export const PROJECT_SUMMARY_STATUS_OPTIONS = ["Active", "On Hold", "Complete", "Cancelled"];
export const PROJECT_SUMMARY_PHASE_OPTIONS = [
  "Sales",
  "Finance",
  "Drafting",
  "Permitting",
  "Construction",
  "Fiber",
  "Networking",
  "Accounting",
];
export const DEPARTMENT_OPTIONS = ["Engineering", "Construction", "Fiber", "Networking", "Accounting"];

export const DAILY_PROGRESS_REPORTS_FIELD = {
  key: "daily_progress_reports",
  label: "Daily progress reports",
  type: "repeater",
  rowLabel: "Report",
  fields: [
    { key: "date", label: "Date", type: "date" },
    { key: "conduit_footage", label: "Conduit footage", type: "number" },
    { key: "fiber_footage", label: "Fiber footage", type: "number" },
    { key: "proofing_footage", label: "Proofing completed (ft)", type: "number" },
    { key: "vaults", label: "Vaults placed", type: "number" },
    { key: "contractor", label: "Contractor", type: "text" },
    { key: "notes", label: "Notes", type: "textarea" },
    { key: "reported_by", label: "Reported by", type: "readonly" },
    { key: "report_source", label: "Report source", type: "readonly" },
  ],
};

const DEPARTMENT_LEGACY_ALIASES = {
  splicing: "Fiber",
  splice: "Fiber",
};
export const CONNECTION_OPTIONS = ["DIA", "PTP", "Dark", "VoIP", "Wave"];
export const HANDOFF_OPTIONS = ["Fiber", "Copper"];
export const IP_OPTIONS = ["30", "29", "28", "27", "26", "25", "24"];

/** Stored slug → display label for service speed selects. */
export const SERVICE_SPEED_OPTIONS = [
  { value: "na", label: "N/A" },
  { value: "10m", label: "10 Mb" },
  { value: "50m", label: "50 Mb" },
  { value: "100m", label: "100 Mb" },
  { value: "150m", label: "150 Mb" },
  { value: "200m", label: "200 Mb" },
  { value: "250m", label: "250 Mb" },
  { value: "300m", label: "300 Mb" },
  { value: "350m", label: "350 Mb" },
  { value: "400m", label: "400 Mb" },
  { value: "500m", label: "500 Mb" },
  { value: "600m", label: "600 Mb" },
  { value: "1g", label: "1 Gb" },
  { value: "2g", label: "2 Gb" },
  { value: "2_5g", label: "2.5 Gb" },
  { value: "3g", label: "3 Gb" },
  { value: "5g", label: "5 Gb" },
  { value: "6g", label: "6 Gb" },
  { value: "25g", label: "25 Gb" },
  { value: "10g", label: "10 Gb" },
  { value: "20g", label: "20 Gb" },
  { value: "30g", label: "30 Gb" },
  { value: "40g", label: "40 Gb" },
  { value: "50g", label: "50 Gb" },
  { value: "100g", label: "100 Gb" },
];

const SERVICE_SPEED_LABEL_BY_VALUE = new Map(SERVICE_SPEED_OPTIONS.map((o) => [o.value, o.label]));

const SERVICE_SPEED_LEGACY_ALIASES = new Map(
  [
    ...SERVICE_SPEED_OPTIONS.flatMap((o) => [
      [o.label.toLowerCase(), o.value],
      [o.value.toLowerCase(), o.value],
    ]),
    ["n/a", "na"],
    ["10mbps", "10m"],
    ["100mbps", "100m"],
    ["1gbps", "1g"],
    ["10gbps", "10g"],
    ["100gbps", "100g"],
  ].map(([k, v]) => [String(k).trim().toLowerCase(), v])
);

/** Canonical slug for selects and storage (maps legacy labels/slugs when possible). */
export function serviceSpeedSlug(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (SERVICE_SPEED_LABEL_BY_VALUE.has(s)) return s;
  const lower = s.toLowerCase();
  if (SERVICE_SPEED_LABEL_BY_VALUE.has(lower)) return lower;
  return SERVICE_SPEED_LEGACY_ALIASES.get(lower) ?? s;
}

/** Human-readable label for grid, read views, and emails. */
export function serviceSpeedLabel(raw) {
  const slug = serviceSpeedSlug(raw);
  if (SERVICE_SPEED_LABEL_BY_VALUE.has(slug)) return SERVICE_SPEED_LABEL_BY_VALUE.get(slug);
  const trimmed = String(raw ?? "").trim();
  return trimmed || "";
}

/** Normalize string or { value, label } option lists for select rendering. */
export function normalizeSelectOptions(options) {
  return (options || []).map((o) =>
    o != null && typeof o === "object" && "value" in o
      ? { value: String(o.value), label: String(o.label ?? o.value) }
      : { value: String(o), label: String(o) }
  );
}

/** @deprecated Use subphaseSelectOptionsForPhase from commercialSubphases.js */
export const SUBPHASE_BY_PHASE = {};

const fiberFileRowFields = [
  { key: "equipment_shelter", label: "Equipment shelter", type: "text" },
  { key: "port_details", label: "Port details", type: "text" },
  { key: "dlr", label: "DLR", type: "file", accept: ".pdf" },
  { key: "otdr", label: "OTDR", type: "file", accept: ".pdf" },
];

const networkingInstallationRowFields = [
  { key: "location", label: "Location", type: "textarea" },
  {
    key: "technician",
    label: "Technician",
    type: "user_role_select",
    roleNames: ["Network Technician", "Network Manager"],
  },
  { key: "start_date", label: "Start date", type: "date" },
  { key: "end_date", label: "End date", type: "date" },
  { key: "equipment_notes", label: "Equipment required", type: "textarea" },
  { key: "voip_installation", label: "VoIP installation", type: "bool" },
  {
    key: "complete",
    label: "Installation complete",
    type: "bool",
    hint: "Technician, Network Manager, or coordinator marks when the install is done.",
    showWhen: (ctx) => {
      const summary = ctx?.summary || {};
      const subphases =
        summary.subphases && typeof summary.subphases === "object" ? summary.subphases : {};
      const raw = subphases.networking_subphase ?? summary.subphase ?? "";
      const slug = String(raw).trim().toLowerCase().replace(/\s+/g, "_");
      return slug === "installation_scheduled" || slug === "pending_service_activation";
    },
  },
  {
    key: "schedule_email_sent",
    label: "Schedule email sent",
    type: "bool",
    hidden: true,
  },
];

export const COMMERCIAL_DOMAIN_SCHEMAS = {
  [COMMERCIAL_DOMAINS.SUMMARY]: {
    id: COMMERCIAL_DOMAINS.SUMMARY,
    label: "Project summary",
    fields: [
      { key: "work_order", label: "Work order", type: "text" },
      { key: "customer", label: "Customer", type: "text" },
      { key: "new_customer", label: "New customer", type: "bool" },
      { key: "project", label: "Project", type: "text" },
      { key: "type", label: "Type", type: "select", options: PROJECT_SUMMARY_TYPE_OPTIONS },
      {
        key: "subtype",
        label: "Subtype",
        type: "multiselect",
        options: PROJECT_SUMMARY_SUBTYPE_OPTIONS,
        showWhen: commercialNonIncidentFieldVisible,
      },
      { key: "status", label: "Status", type: "select", options: PROJECT_SUMMARY_STATUS_OPTIONS },
      { key: "phase", label: "Phase", type: "select", options: PROJECT_SUMMARY_PHASE_OPTIONS },
      { key: "subphase", label: "Subphase", type: "select", optionsFromPhase: true },
      {
        key: "foc_date",
        label: "FOC date",
        type: "date",
        hint: "Target: 120 days from work order creation",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "sales_rep",
        label: "Sales rep",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
      { key: "departments", label: "Departments", type: "multiselect", options: DEPARTMENT_OPTIONS },
      {
        key: "site_walk",
        label: "Site walk",
        type: "bool",
        showWhen: (ctx) =>
          commercialNonIncidentFieldVisible(ctx) && departmentIncludes(ctx.summary, "Engineering"),
      },
      { key: "service_location", label: "Service location", type: "textarea" },
      {
        key: "onsite_contact_name",
        label: "Site contact name",
        type: "text",
      },
      {
        key: "onsite_contact_email",
        label: "Site contact email",
        type: "text",
      },
      {
        key: "onsite_contact_number",
        label: "Site contact phone",
        type: "text",
      },
      {
        key: "notes",
        label: "Notes",
        type: "textarea",
        hint: "Project notes visible on the overview landing page",
      },
      {
        key: "a_location",
        label: "A location",
        type: "textarea",
        showWhen: (ctx) => connectionIncludes(ctx.networking, ["PTP", "Dark"]),
      },
      {
        key: "z_location",
        label: "Z location",
        type: "textarea",
        showWhen: (ctx) => connectionIncludes(ctx.networking, ["PTP", "Dark"]),
      },
      {
        key: "service_order",
        label: "Service order",
        type: "file",
        accept: ".pdf",
        showWhen: (ctx) =>
          commercialNonIncidentFieldVisible(ctx) &&
          isRevenueCommercialProjectType(ctx.summary?.type ?? ctx.summary?.work_order_type),
      },
      {
        key: "work_order_excel_template",
        label: "Work order Excel template",
        type: "file",
        accept: ".xlsx,.xls",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "signed_work_order",
        label: "Signed work order",
        type: "file",
        accept: ".pdf",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "executed_service_order",
        label: "Executed service order",
        type: "file",
        accept: ".pdf",
        showWhen: (ctx) =>
          commercialNonIncidentFieldVisible(ctx) &&
          isRevenueCommercialProjectType(ctx.summary?.type ?? ctx.summary?.work_order_type),
      },
      {
        key: "incident",
        label: "Fiber hit / incident",
        type: "bool",
        hint: "Enable for fiber hits, third-party outages, and similar non-service incident work (e.g. 13227, 14114).",
        showWhen: commercialIncidentToggleVisible,
      },
      {
        key: "incident_type",
        label: "Incident type",
        type: "select",
        options: INCIDENT_TYPE_OPTIONS,
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "incident_date",
        label: "Incident date",
        type: "date",
        hint: "When the hit or outage occurred",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "damage_location",
        label: "Damage location",
        type: "textarea",
        hint: "Address, intersection, or GPS where damage occurred",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "responsible_party",
        label: "Responsible party",
        type: "text",
        hint: "Who caused the damage or is liable",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "impacted_network",
        label: "Impacted network",
        type: "textarea",
        hint: "Circuits, cables, customers, or network segments affected",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "incident_contractor",
        label: "Contractor (if applicable)",
        type: "text",
        hint: "Company or crew working the repair",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "incident_contractor_contact",
        label: "Contractor contact",
        type: "text",
        hint: "Phone or email for the contractor",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "incident_photos",
        label: "Incident photos",
        type: "multifile",
        accept: ".png,.jpeg,.jpg,.webp,.pdf",
        hint: "Photos of damage, dig site, or restoration",
        showWhen: commercialIncidentDetailsVisible,
      },
      {
        key: "incident_notes",
        label: "Incident notes",
        type: "textarea",
        showWhen: commercialIncidentDetailsVisible,
      },
    ],
  },
  [COMMERCIAL_DOMAINS.ENGINEERING]: {
    id: COMMERCIAL_DOMAINS.ENGINEERING,
    label: "Engineering",
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Engineering"),
    fields: [
      {
        key: "preliminary_route",
        label: "Route",
        type: "file",
        accept: ".kmz,.kml",
        editRoles: ENGINEERING_ROUTE_UPLOAD_ROLE_PATTERNS,
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_footage",
        label: "Construction footage",
        type: "number",
        hint: "Planned install footage (ft) for this work order",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "site_walk_files",
        label: "Site walk files",
        type: "multifile",
        accept: ".pdf,.png,.jpeg,.jpg",
        editRoles: [...OSP_ENGINEERING_SITE_WALK_EDIT_ROLE_PATTERNS],
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "asbuilt",
        label: "As-built",
        type: "multifile",
        accept: ".pdf",
        editRoles: ASBUILT_EDIT_ROLE_PATTERNS,
        /** Stored on construction_details; shown on Engineering for OSP (no Construction tab). */
        valueDomain: COMMERCIAL_DOMAINS.CONSTRUCTION,
        showWhen: (ctx) => canEditOspEngineeringSiteWalkFiles(ctx?.viewerEmail),
      },
      {
        key: "drafter",
        label: "Drafter",
        type: "user_role_select",
        roleNames: ["Drafter", "Engineering Supervisor", "Engineering Manager"],
        roleTypeIds: ["role_mokc6r8s_5skrub", "role_eng_supervisor_pb01", "role_mokc6l2i_tpgjpy"],
        editRoles: ENGINEERING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS,
      },
      {
        ...PERMITS_REPEATER_FIELD,
        editRoles: ENGINEERING_PERMITS_EDIT_ROLE_PATTERNS,
      },
      {
        key: "permit_coverage_confirmation",
        label: "Permit coverage confirmation",
        type: "file",
        accept: ".pdf,.png,.jpeg,.jpg,.eml,.msg",
        // Edited via PermitCoveragePanel on expansion Permitting tab; view-only on Engineering/Construction.
        showWhen: () => false,
      },
    ],
  },
  [COMMERCIAL_DOMAINS.CONSTRUCTION]: {
    id: COMMERCIAL_DOMAINS.CONSTRUCTION,
    label: "Construction",
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Construction"),
    fields: [
      {
        key: "construction_start_date",
        label: "Planned start",
        type: "date",
        hint: "Scheduled construction start",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_days_to_complete",
        label: "Days to complete",
        type: "number",
        hint: "Planned duration in weekdays (Mon–Fri). Enable Include weekends to count all calendar days.",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_include_weekends",
        label: "Include weekends",
        type: "bool",
        hint: "When enabled, days to complete and planned end include Saturdays and Sundays.",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_end_date",
        label: "Planned end",
        type: "date",
        hint: "Scheduled construction end",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_actual_start_date",
        label: "Actual start",
        type: "date",
        hint: "When construction work actually began",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_actual_end_date",
        label: "Actual end",
        type: "date",
        hint: "When construction was completed",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_laborers",
        label: "Construction laborers",
        type: "user_role_multiselect",
        roleName: "Construction Laborer",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_foremen",
        label: "Construction leads",
        type: "user_role_multiselect",
        roleName: "Construction Lead",
        showWhen: commercialNonIncidentFieldVisible,
      },
      { key: "bid_out", label: "Bid out", type: "bool", showWhen: commercialNonIncidentFieldVisible },
      {
        key: "inspector",
        label: "Inspector",
        type: "user_role_select",
        roleName: "Inspector",
        editRoles: [...INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS],
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "construction_contractor",
        label: "Construction contractor",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "fiber_contractor",
        label: "Fiber contractor",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        ...DAILY_PROGRESS_REPORTS_FIELD,
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "asbuilt",
        label: "As-built",
        type: "multifile",
        accept: ".pdf",
        editRoles: ASBUILT_EDIT_ROLE_PATTERNS,
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "damage_report",
        label: "Damage report",
        type: "multifile",
        accept: ".pdf,.png,.jpeg,.jpg,.webp",
        hint: "Upload damage report or related documentation",
        showWhen: commercialIncidentOnlyFieldVisible,
      },
      {
        key: "usa_ticket",
        label: "811 / USA ticket",
        type: "multifile",
        accept: ".pdf",
        editRoles: USA_TICKET_EDIT_ROLE_PATTERNS,
      },
    ],
  },
  [COMMERCIAL_DOMAINS.FIBER]: {
    id: COMMERCIAL_DOMAINS.FIBER,
    label: "Fiber",
    sectionVisible: (ctx) => departmentIncludesFiber(ctx.summary),
    fields: [
      { key: "splicing_start_date", label: "Splicing start", type: "date" },
      { key: "splicing_end_date", label: "Splicing end", type: "date" },
      {
        key: "splicer",
        label: "Splicer",
        type: "user_role_select",
        roleName: "Splicer",
      },
      {
        key: "fiber_files",
        label: "Fiber files",
        type: "repeater",
        rowLabel: "Fiber run",
        fields: fiberFileRowFields,
      },
    ],
  },
  [COMMERCIAL_DOMAINS.NETWORKING]: {
    id: COMMERCIAL_DOMAINS.NETWORKING,
    label: "Networking",
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Networking"),
    fields: [
      {
        key: "circuit_id",
        label: "Circuit ID",
        type: "text",
        hint: "Leave blank to auto-generate from the work order (CVIN-…).",
      },
      { key: "service_speed", label: "Service speed", type: "select", options: SERVICE_SPEED_OPTIONS },
      { key: "connection", label: "Connection", type: "multiselect", options: CONNECTION_OPTIONS },
      {
        key: "a_location",
        label: "A location",
        type: "textarea",
        /** Stored on project_summary; shown here when Connection includes PTP or Dark. */
        valueDomain: COMMERCIAL_DOMAINS.SUMMARY,
        showWhen: (ctx) => connectionIncludes(ctx.networking, ["PTP", "Dark"]),
      },
      {
        key: "z_location",
        label: "Z location",
        type: "textarea",
        valueDomain: COMMERCIAL_DOMAINS.SUMMARY,
        showWhen: (ctx) => connectionIncludes(ctx.networking, ["PTP", "Dark"]),
      },
      { key: "handoff", label: "Handoff", type: "select", options: HANDOFF_OPTIONS },
      { key: "ips", label: "IPs", type: "select", options: IP_OPTIONS },
      { key: "nid_config", label: "NID config", type: "file", accept: ".txt,text/plain" },
      { key: "topology", label: "Topology", type: "file", accept: ".txt,text/plain" },
      { key: "cut_sheet", label: "Cut sheet", type: "file", accept: ".pdf" },
      {
        key: "installations",
        label: "Installations",
        type: "repeater",
        rowLabel: "Installation",
        fields: networkingInstallationRowFields,
      },
      { key: "it_contact", label: "IT contact", type: "text" },
      { key: "it_number", label: "IT phone", type: "text" },
      { key: "it_email", label: "IT email", type: "text" },
    ],
  },
  [COMMERCIAL_DOMAINS.ACCOUNTING]: {
    id: COMMERCIAL_DOMAINS.ACCOUNTING,
    label: "Accounting",
    fields: [
      {
        key: "completion_notice",
        label: "Completion notice",
        type: "file",
        accept: ".pdf",
        showWhen: commercialNonIncidentFieldVisible,
      },
      { key: "mrc", label: "MRC", type: "currency", showWhen: commercialNonIncidentFieldVisible },
      { key: "nrc", label: "NRC", type: "currency", showWhen: commercialNonIncidentFieldVisible },
      {
        key: "term",
        label: "Term (months)",
        type: "number",
        showWhen: commercialNonIncidentFieldVisible,
      },
      { key: "invoiced", label: "Invoiced", type: "date" },
      {
        key: "accounting_contact",
        label: "Accounting contact",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "accounting_number",
        label: "Accounting phone",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
      {
        key: "accounting_email",
        label: "Accounting email",
        type: "text",
        showWhen: commercialNonIncidentFieldVisible,
      },
    ],
  },
};

export const PROJECT_LANDING_TAB_TO_DOMAIN = {
  summary: COMMERCIAL_DOMAINS.SUMMARY,
  engineering: COMMERCIAL_DOMAINS.ENGINEERING,
  permitting: COMMERCIAL_DOMAINS.ENGINEERING,
  construction: COMMERCIAL_DOMAINS.CONSTRUCTION,
  fiber: COMMERCIAL_DOMAINS.FIBER,
  networking: COMMERCIAL_DOMAINS.NETWORKING,
  accounting: COMMERCIAL_DOMAINS.ACCOUNTING,
};

export function departmentIncludes(summary, name) {
  const deps = normalizeDepartments(summary?.departments);
  return deps.some((d) => String(d).toLowerCase() === String(name).toLowerCase());
}

export function departmentIncludesFiber(summary) {
  const deps = normalizeDepartments(summary?.departments);
  return deps.some((d) => {
    const t = String(d).toLowerCase();
    return t === "fiber" || t === "splicing";
  });
}

export function connectionIncludes(networking, names) {
  const conn = normalizeMulti(networking?.connection);
  const want = names.map((n) => n.toLowerCase());
  return conn.some((c) => want.includes(String(c).toLowerCase()));
}

/** Strip Excel/CSV quote artifacts from one list token (`"Engineering` → `Engineering`). */
export function cleanCsvListToken(s) {
  let t = String(s ?? "")
    .trim()
    .replace(/^\uFEFF/, "")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'");
  t = t.replace(/^\[+/, "").replace(/\]+$/, "").trim();
  while (t.length && (t.startsWith('"') || t.startsWith("'"))) {
    t = t.slice(1).trim();
  }
  while (t.length && (t.endsWith('"') || t.endsWith("'"))) {
    t = t.slice(0, -1).trim();
  }
  return t.trim();
}

function mapCleanTokens(items) {
  return items
    .map((x) => cleanCsvListToken(x))
    .filter((x) => x !== "");
}

/** ACF / WP often stores lists as arrays or numeric-key objects. */
function multiValueToArray(v) {
  if (Array.isArray(v)) {
    return mapCleanTokens(v.filter((x) => x != null && String(x).trim() !== ""));
  }
  if (v != null && typeof v === "object") {
    const keys = Object.keys(v).filter((k) => /^\d+$/.test(k));
    if (keys.length) {
      return mapCleanTokens(
        keys
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => v[k])
          .filter((x) => x != null && String(x).trim() !== "")
      );
    }
  }
  if (v == null || v === "") return [];
  return mapCleanTokens(String(v).split(/[,;|]/));
}

/** Parse one CSV cell into a list (commas, JSON arrays, numeric-key objects). */
export function parseMultiFromCsvCell(raw) {
  if (raw == null || raw === "") return [];
  let s = cleanCsvListToken(raw);
  s = s.replace(/\uFF0C/g, ",").replace(/[\u2018\u2019]/g, "'");
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return multiValueToArray(parsed);
    } catch {
      /* fall through */
    }
  }
  const pseudoJson = /"\s*,\s*"/.test(s) || /^"[^"]*"(,\s*"[^"]*")+$/.test(s);
  if (pseudoJson) {
    try {
      const parsed = JSON.parse(`[${s}]`);
      if (Array.isArray(parsed)) return multiValueToArray(parsed);
    } catch {
      /* fall through */
    }
  }
  return multiValueToArray(s);
}

export function canonicalizeDepartmentName(label) {
  const t = cleanCsvListToken(label);
  if (!t) return null;
  const low = t.toLowerCase();
  if (DEPARTMENT_LEGACY_ALIASES[low]) return DEPARTMENT_LEGACY_ALIASES[low];
  const match = DEPARTMENT_OPTIONS.find((o) => o.toLowerCase() === low);
  return match || null;
}

/** Ordered unique department labels for import / storage. */
export function canonicalizeDepartmentList(values) {
  const raw = Array.isArray(values) ? multiValueToArray(values) : parseMultiFromCsvCell(values);
  const picked = [];
  for (const v of raw) {
    const c = canonicalizeDepartmentName(v);
    if (c && !picked.includes(c)) picked.push(c);
  }
  return DEPARTMENT_OPTIONS.filter((o) => picked.includes(o));
}

export function normalizeDepartments(v) {
  const arr = multiValueToArray(v);
  if (!arr.length) return [];
  const canon = canonicalizeDepartmentList(arr);
  return canon.length ? canon : arr;
}

export function normalizeMulti(v) {
  return multiValueToArray(v);
}

/** Map stored labels onto canonical multiselect options (case-insensitive). */
export function multiselectValuesForOptions(value, options) {
  const raw = normalizeMulti(value);
  const opts = Array.isArray(options) ? options : [];
  const picked = [];
  for (const r of raw) {
    const t = String(r).trim();
    if (!t) continue;
    const match = opts.find((o) => String(o).toLowerCase() === t.toLowerCase());
    if (match && !picked.includes(match)) picked.push(match);
  }
  return picked;
}

export function emptyCommercialAcfGroups() {
  const out = {};
  for (const k of COMMERCIAL_DOMAIN_LIST) out[k] = {};
  return out;
}

export function computeCircuitId(summary, networking) {
  const stored =
    networking?.circuit_id != null && String(networking.circuit_id).trim() !== ""
      ? String(networking.circuit_id).trim()
      : "";
  if (stored) return stored;

  const wo = summary?.work_order != null ? String(summary.work_order).trim() : "";
  if (!wo) return "";

  const dotIdx = wo.indexOf(".");
  if (dotIdx >= 0) {
    const base = wo.slice(0, dotIdx);
    const suffix = wo.slice(dotIdx + 1);
    if (!base || !suffix) return "";
    return `CVIN-${base}-${suffix}`;
  }
  return `CVIN-${wo}-00`;
}

export function commercialFieldVisible(field, ctx) {
  if (field.hidden) return false;
  if (!field.showWhen) {
    // fall through to accessRoles check
  } else if (!field.showWhen(ctx)) {
    return false;
  }
  if (Array.isArray(field.accessRoles) && field.accessRoles.length > 0) {
    return expansionFieldRoleVisible(field, ctx?.viewerEmail);
  }
  if (field.derivedFromContract) {
    return expansionContractFieldVisible(field, ctx?.viewerEmail);
  }
  return true;
}
